import { Dialog, DialogActions, DialogContent, Fade } from '@mui/material';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { DataTable } from 'src/components/DataTable';
import { WorkerProfile } from 'src/DTO/WorkerProfile.type';
import { getWorkerColumns } from 'src/hooks/useWorkerColumns';
import { InviteWorkersForm } from 'src/pages/Workers/components/InviteWorkersForm';
import { CreateJobOfferModalProps } from './CreateJobForm.types';
import { WorkerScope } from 'src/api/hooks/useWorker.types';
import { toggleWorkers } from 'src/utils/toggleWorkers';
import { useWorkersData } from 'src/hooks/useWorkersData';

export const CreateJobOfferModal: React.FC<CreateJobOfferModalProps> = ({
  isOpen,
  setIsOpen,
  workersToOfferJob,
  setWorkersToOfferJob,
  onSubmit,
  jobId,
}) => {
  const { t } = useTranslation();

  // LOCAL STATE
  const [isInviteFormOpen, setIsInviteFormOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const {
    fetchWorkers,
    filteredWorkersCount,
    finalizedWorkers,
    workersLoading,
    totalPageCount,
    totalWorkersCount,
  } = useWorkersData({ scope: WorkerScope.INVITED, jobId, isVisible: isOpen });

  // GLOBAL STATE

  const handleToggle = (worker: WorkerProfile) => {
    toggleWorkers(worker, workersToOfferJob, setWorkersToOfferJob);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsInviteFormOpen(false);
  };

  const columns = getWorkerColumns({
    selectedWorkers: workersToOfferJob,
    handleToggle,
  });

  const isSubmitDisabled = workersToOfferJob.length === 0;

  return (
    <Dialog
      TransitionComponent={Fade}
      transitionDuration={{ enter: 300, exit: 0 }}
      open={isOpen}
      onClose={handleClose}
      fullWidth
      keepMounted
      maxWidth={isInviteFormOpen ? 'sm' : 'xxl'}
      PaperProps={{
        sx: {
          width: '100%',
          padding: '10px',
        },
      }}>
      <DialogContent>
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          {isInviteFormOpen ? (
            <InviteWorkersForm
              onCancel={() => setIsInviteFormOpen(false)}
              onSuccess={worker => {
                handleToggle(worker);
                fetchWorkers({ page: 1 });
                setResetKey(prev => prev + 1);
              }}
            />
          ) : (
            <DataTable<WorkerProfile>
              key={resetKey}
              title={t('jobPage.inviteWorkersModal.title')}
              data={finalizedWorkers}
              columns={columns}
              isLoading={workersLoading}
              serverSide
              pageInfo={{
                totalPageCount,
                totalRowCount: totalWorkersCount,
                filteredRowCount: filteredWorkersCount,
              }}
              onRefetchData={(page, filtersArray) => fetchWorkers({ page, filtersArray })}
              selectedRowCount={workersToOfferJob.length}
            />
          )}
        </Box>
      </DialogContent>

      {!isInviteFormOpen && (
        <Box>
          <DialogActions sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={BUTTON_VARIANT.GRADIENT}
                  color={BUTTON_COLOR.DARK}
                  type="submit"
                  onClick={() => setWorkersToOfferJob([])}
                  disabled={workersLoading || workersToOfferJob.length === 0}>
                  <Trans>jobPage.inviteWorkersModal.resetSelectionButton</Trans>
                </Button>
                <Button
                  variant={BUTTON_VARIANT.GRADIENT}
                  color={BUTTON_COLOR.DARK}
                  type="submit"
                  onClick={() => setIsInviteFormOpen(true)}
                  disabled={workersLoading}>
                  <Trans>jobPage.inviteWorkersModal.addNewStudentButton</Trans>
                </Button>
              </Box>
              <Button
                variant={BUTTON_VARIANT.GRADIENT}
                color={BUTTON_COLOR.DARK}
                type="submit"
                onClick={() => {
                  !!onSubmit && onSubmit();
                  handleClose();
                }}
                disabled={workersLoading || isSubmitDisabled}>
                <Trans>jobPage.inviteWorkersModal.submitButton</Trans>
              </Button>
            </Box>
          </DialogActions>
        </Box>
      )}
    </Dialog>
  );
};
